import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert, Carousel, Tab, Tabs } from "react-bootstrap";
import EventShareButtons from "../components/EventShareButtons";
import CommunicationButtons from "../components/CommunicationButtons";
import CustomerSupportWidget from "../components/CustomerSupportWidget";
import { fetchProgramById, fetchProgramByCode, fetchProgramMembers } from "../services/ProgramService";
import { fetchGroupsByProgram } from "../services/GroupService";
import { fetchLanguages } from "../services/LocationService";
import LanguageData from "../models/LanguageData";

import ProgramData from "../models/ProgramData";
import { IMAGE_BASE } from "../Config";

const PlayDetailPageWrapper: React.FC = () => {
  const { id, playcode } = useParams<{ id?: string; playcode?: string }>();
  const [program, setProgram] = useState<ProgramData | null>(null);
  const [groups, setGroups] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [languages, setLanguages] = useState<LanguageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id && !playcode) return;
      
      try {
        setLoading(true);
        let programData;
        
        // Fetch program by ID or code
        if (id) {
          programData = await fetchProgramById(id);
        } else if (playcode) {
          programData = await fetchProgramByCode(playcode);
        }
        
        if (!programData) {
          throw new Error("Program not found");
        }

        const [groupsData, membersData, languagesData] = await Promise.all([
          fetchGroupsByProgram(programData._id),
          fetchProgramMembers(programData._id),
          fetchLanguages()
        ]);
        
        setProgram(programData);
        setGroups(Array.isArray(groupsData) ? groupsData : []);
        setMembers(Array.isArray(membersData) ? membersData : []);
        setLanguages(Array.isArray(languagesData) ? languagesData : []);
      } catch (err) {
        setError("Failed to load play details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, playcode]);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="warning" />
        <p className="mt-3">Loading play details...</p>
      </Container>
    );
  }

  if (error || !program) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Play Not Found</h4>
          <p>{error || "The requested play could not be found."}</p>
          <Link to="/groups" className="btn btn-warning">Back to Groups</Link>
        </Alert>
      </Container>
    );
  }

  const images = [program.image1, program.image2, program.image3].filter(Boolean);
  const averageRating = program.reviews?.length 
    ? program.reviews.reduce((sum, review) => sum + review.rating, 0) / program.reviews.length 
    : 0;

  // Generate shareable URL using programCode
  const shareableUrl = `${window.location.origin}/play/${program.programCode}`;

  return (
    <Container className="py-4">
      {/* Breadcrumb Navigation */}
      <Row className="mb-3">
        <Col xs={12}>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/groups" className="text-decoration-none">Groups</Link>
              </li>
              {groups.length > 0 && (
                <li className="breadcrumb-item">
                  <Link to={`/group/${groups[0]._id}`} className="text-decoration-none">{groups[0].name}</Link>
                </li>
              )}
              <li className="breadcrumb-item active" aria-current="page">
                {program.title}
              </li>
            </ol>
          </nav>
        </Col>
      </Row>

      {/* Hero Section */}
      <Row className="mb-5">
        <Col lg={6} className="mb-4">
          {images.length > 0 ? (
            <Carousel className="shadow-lg rounded">
              {images.map((image, index) => (
                <Carousel.Item key={index}>
                  <img
                    src={IMAGE_BASE + image}
                    alt={`${program.title} - Image ${index + 1}`}
                    className="d-block w-100"
                    style={{ height: "400px", objectFit: "cover" }}
                  />
                </Carousel.Item>
              ))}
            </Carousel>
          ) : (
            <div 
              className="bg-gradient d-flex align-items-center justify-content-center rounded shadow-lg"
              style={{ height: "400px", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
            >
              <i className="bi bi-play-circle text-white" style={{ fontSize: "5rem" }}></i>
            </div>
          )}
        </Col>
        <Col lg={6}>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h1 className="display-5 fw-bold text-dark mb-2">{program.title}</h1>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {program.isFeatured && (
                  <Badge bg="warning" text="dark" className="px-3 py-2">
                    <i className="bi bi-star-fill me-1"></i>Featured
                  </Badge>
                )}
                <Badge bg={program.status === 'running' ? 'success' : program.status === 'upcoming' ? 'primary' : 'secondary'} className="px-3 py-2">
                  {program.status?.toUpperCase() || 'UPCOMING'}
                </Badge>
              </div>
            </div>
            {averageRating > 0 && (
              <div className="text-center">
                <div className="h4 text-warning mb-0">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className={`bi bi-star${i < Math.round(averageRating) ? '-fill' : ''}`}></i>
                  ))}
                </div>
                <small className="text-muted">{averageRating.toFixed(1)}/5 ({program.reviews?.length} reviews)</small>
              </div>
            )}
          </div>
          
          <p className="lead text-muted mb-4">{program.description}</p>
          
          <Row className="g-3 mb-4">
            <Col md={6}>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-clock text-warning me-2"></i>
                <span><strong>Duration:</strong> {program.durationHours}h {program.durationMinutes}m</span>
              </div>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-translate text-warning me-2"></i>
                <span><strong>Language:</strong> {
                  languages.find(l => l.code === program.language)?.nativeName || 
                  languages.find(l => l.code === program.language)?.name || 
                  program.language || "N/A"
                }</span>
              </div>
              {program.director && (
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-person-badge text-warning me-2"></i>
                  <span><strong>Director:</strong> {program.director}</span>
                </div>
              )}
            </Col>
            <Col md={6}>
              {program.writer && (
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-pen text-warning me-2"></i>
                  <span><strong>Writer:</strong> {program.writer}</span>
                </div>
              )}
              {groups.length > 0 && (
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-people text-warning me-2"></i>
                  <span><strong>Theater Group:</strong> 
                    {groups.map((group, index) => (
                      <span key={group._id}>
                        <Link 
                          to={`/group/${group._id}`} 
                          className="text-decoration-none text-warning fw-semibold ms-1"
                        >
                          {group.name}
                        </Link>
                        {index < groups.length - 1 && ", "}
                      </span>
                    ))}
                  </span>
                </div>
              )}
              {program.ageRating && (
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-shield-check text-warning me-2"></i>
                  <span><strong>Age Rating:</strong> {program.ageRating}</span>
                </div>
              )}
              {program.venue && (
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-geo-alt text-warning me-2"></i>
                  <span><strong>Venue:</strong> {program.venue.name}</span>
                </div>
              )}
            </Col>
          </Row>

          {/* Ticket Prices */}
          {program.ticketPrices && program.ticketPrices.length > 0 && (
            <div className="mb-4">
              <h6 className="fw-bold mb-2">Ticket Prices:</h6>
              <div className="d-flex flex-wrap gap-2">
                {program.ticketPrices.map((ticket, index) => (
                  <Badge key={index} bg="outline-success" text="dark" className="px-3 py-2">
                    {ticket.category}: {ticket.currency} {ticket.price}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="d-flex flex-wrap gap-3">
            <Link to={`/play/${program._id}/schedule`} className="btn btn-warning btn-lg px-4">
              <i className="bi bi-calendar-event me-2"></i>Check Booking Schedules
            </Link>
            <EventShareButtons
              event={{
                title: program.title,
                date: "Check schedules for dates",
                time: `${program.durationHours}h ${program.durationMinutes}m`,
                venue: program.venue?.name || "Various venues"
              }}
              bookingUrl={shareableUrl}
              variant="outline-success"
              size="lg"
            />
            {groups.length > 0 && groups[0].contactPhone && (
              <CommunicationButtons
                variant="contact"
                size="lg"
                data={{
                  organizerName: groups[0].name,
                  organizerPhone: groups[0].contactPhone,
                  eventName: program.title,
                  eventDate: "Check schedules",
                  eventTime: `${program.durationHours}h ${program.durationMinutes}m`,
                  venue: program.venue?.name || "Various venues",
                  customerName: "Interested Customer",
                  customerPhone: "",
                  customerEmail: ""
                }}
              />
            )}
          </div>
        </Col>
      </Row>

      {/* Content Tabs */}
      <Tabs defaultActiveKey="synopsis" className="mb-4">
        {/* Synopsis Tab */}
        <Tab eventKey="synopsis" title="Synopsis">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <p className="lead">{program.synopsis || program.description || "No synopsis available."}</p>
              {program.warnings && program.warnings.length > 0 && (
                <div className="mt-3">
                  <h6 className="text-warning"><i className="bi bi-exclamation-triangle me-2"></i>Content Warnings:</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {program.warnings.map((warning, index) => (
                      <Badge key={index} bg="warning" text="dark">{warning}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Cast & Crew Tab */}
        <Tab eventKey="cast" title="Cast & Crew">
          <Row className="g-4">
            {members.length > 0 ? (
              <>
                {/* Cast Members */}
                <Col md={6}>
                  <h5 className="fw-bold mb-3"><i className="bi bi-people text-warning me-2"></i>Cast</h5>
                  {members.filter(member => 
                    member.memberTypeId?.name === 'Actor' || 
                    member.memberTypeId?.name === 'Singer' || 
                    member.memberTypeId?.name === 'Dancer'
                  ).map((member, index) => (
                    <Card key={index} className="mb-3 border-0 shadow-sm">
                      <Card.Body className="d-flex align-items-center">
                        <div 
                          className="rounded-circle bg-light d-flex align-items-center justify-content-center me-3"
                          style={{ width: "60px", height: "60px" }}
                        >
                          <i className="bi bi-person text-muted"></i>
                        </div>
                        <div>
                          <h6 className="mb-1">{member.firstName} {member.lastName}</h6>
                          <small className="text-muted">{member.memberTypeId?.name}</small>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </Col>
                
                {/* Crew Members */}
                <Col md={6}>
                  <h5 className="fw-bold mb-3"><i className="bi bi-tools text-warning me-2"></i>Crew</h5>
                  {members.filter(member => 
                    member.memberTypeId?.name !== 'Actor' && 
                    member.memberTypeId?.name !== 'Singer' && 
                    member.memberTypeId?.name !== 'Dancer'
                  ).map((member, index) => (
                    <Card key={index} className="mb-3 border-0 shadow-sm">
                      <Card.Body>
                        <h6 className="mb-1">{member.firstName} {member.lastName}</h6>
                        <small className="text-muted">{member.memberTypeId?.name}</small>
                      </Card.Body>
                    </Card>
                  ))}
                </Col>
              </>
            ) : (
              <Col xs={12} className="text-center py-5">
                <i className="bi bi-people display-1 text-muted mb-3"></i>
                <h5 className="text-muted">No cast and crew information available</h5>
              </Col>
            )}
          </Row>
        </Tab>
      </Tabs>

      {/* Awards Section */}
      {program.awards && program.awards.length > 0 && (
        <Row className="mb-5">
          <Col xs={12}>
            <h3 className="fw-bold mb-4"><i className="bi bi-trophy text-warning me-2"></i>Awards & Recognition</h3>
            <Row className="g-3">
              {program.awards.map((award, index) => (
                <Col key={index} md={6} lg={4}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-2">
                        <i className="bi bi-award text-warning me-2"></i>
                        <Badge bg="warning" text="dark">{award.year}</Badge>
                      </div>
                      <Card.Title className="h6">{award.title}</Card.Title>
                      <Card.Text className="small text-muted">{award.organization}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      )}
      
      {/* Customer Support Widget */}
      {/* <CustomerSupportWidget position="bottom-right" /> */}
    </Container>
  );
};

export default PlayDetailPageWrapper;